import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';

// Styled components
const HeaderContainer = styled.header.attrs(props => ({
  style: {
    backgroundColor: props.isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
    boxShadow: props.isScrolled ? props.theme.shadows.medium : 'none',
    backdropFilter: props.isScrolled ? 'blur(10px)' : 'none'
  }
}))`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
`;

const HeaderInner = styled.div.attrs(props => ({
  style: {
    padding: props.isScrolled ? '0.75rem 2rem' : '1.5rem 2rem'
  }
}))`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  transition: padding 0.3s ease;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.isScrolled ? '0.75rem 1.5rem' : '1rem 1.5rem'} !important;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.isScrolled ? '0.75rem 1rem' : '1rem 1rem'} !important;
  }
`;

const Logo = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: 40px;
  margin-right: 0.75rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LogoText = styled.div.attrs(props => ({
  style: {
    color: props.isScrolled ? props.theme.colors.primary : props.theme.colors.white
  }
}))`
  font-size: 1.25rem;
  font-weight: 700;
  transition: color 0.3s ease;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const Navigation = styled.nav.attrs(props => ({
  style: {
    top: props.isScrolled ? '60px' : '80px'
  }
}))`
  display: flex;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: fixed;
    left: 0;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.98);
    box-shadow: ${props => props.theme.shadows.medium};
    padding: 1.25rem 0;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s ease;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  margin: 0 0.85rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin: 0;
    width: 100%;
    text-align: center;
  }
`;

const NavLink = styled.a.attrs(props => ({
  style: {
    color: props.isScrolled ? props.theme.colors.text : props.theme.colors.white
  }
}))`
  font-size: 0.95rem;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 0.5rem 0;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    color: ${props => props.theme.colors.text} !important;
    display: block;
    padding: 1rem 0;
    
    &:hover {
      background-color: ${props => props.theme.colors.backgroundAlt};
    }
    
    &:after {
      display: none;
    }
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled(NavLink)`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  &:after {
    display: none;
  }
  
  span {
    margin-left: 5px;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const DropdownContent = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  background-color: white;
  min-width: 180px;
  box-shadow: ${props => props.theme.shadows.medium};
  border-radius: 8px;
  padding: 8px 0;
  z-index: 1001;
  opacity: ${props => props.isOpen ? '1' : '0'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: opacity 0.2s ease, transform 0.2s ease;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: static;
    box-shadow: none;
    width: 100%;
    background-color: ${props => props.theme.colors.backgroundAlt};
    transform: none;
  }
`;

const DropdownItem = styled.a`
  color: ${props => props.theme.colors.text};
  padding: 10px 16px;
  text-decoration: none;
  display: block;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.theme.colors.backgroundAlt};
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    text-align: center;
    padding: 12px 16px;
  }
`;

const DownloadButton = styled.a`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  transition: transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    transform: translateY(-2px);
    background-color: ${props => props.theme.colors.primaryDark};
    box-shadow: ${props => props.theme.shadows.medium};
    color: white;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin: 0.5rem 0;
    width: 80%;
    justify-content: center;
  }
`;

const DonateButton = styled.a`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  transition: transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    transform: translateY(-2px);
    background-color: #ff7a3d; /* Slightly darker than secondary */
    box-shadow: ${props => props.theme.shadows.medium};
    color: white;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin: 0.5rem 0;
    width: 80%;
    justify-content: center;
  }
`;

const MobileMenuButton = styled.button.attrs(props => ({
  style: {
    color: props.isScrolled ? props.theme.colors.text : props.theme.colors.white
  }
}))`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s ease;
  padding: 0.5rem;
  border-radius: 4px;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

// Header component
const Header = () => {
  const { translate } = useLanguage();
  // Check if we're on a content page (not homepage)
  const isContentPage = window.location.pathname !== "/" && window.location.pathname !== "/index.html";
  
  const [isScrolled, setIsScrolled] = useState(isContentPage);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [docsDropdownOpen, setDocsDropdownOpen] = useState(false);
  
  // Handle scroll event to change header style
  useEffect(() => {
    const handleScroll = () => {
      // Only change scrolled state for homepage
      if (!isContentPage) {
        setIsScrolled(window.scrollY > 50);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isContentPage]);
  
  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  // Toggle documentation dropdown
  const toggleDocsDropdown = () => {
    setDocsDropdownOpen(!docsDropdownOpen);
  };
  
  return (
    <HeaderContainer isScrolled={isScrolled}>
      <HeaderInner isScrolled={isScrolled}>
        <Logo href="/">
          <LogoImage 
            src="/wt-logo-original.webp" 
            onError={(e) => { e.target.onerror = null; e.target.src = "/wt-logo-original.png"; }}
            alt="WeatherTeller Logo" 
          />
          <LogoText isScrolled={isScrolled}>WeatherTeller</LogoText>
        </Logo>
        
        <MobileMenuButton 
          onClick={toggleMenu}
          isScrolled={isScrolled}
          aria-label="Toggle navigation menu"
        >
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <Navigation isOpen={isMenuOpen} isScrolled={isScrolled}>
          <NavList>
            <NavItem>
              <NavLink href="/#features" isScrolled={isScrolled}>
                {translate('nav.features', 'Features')}
              </NavLink>
            </NavItem>
            
            <NavItem>
              <NavLink href="/#data" isScrolled={isScrolled}>
                {translate('nav.data', 'Weather Data')}
              </NavLink>
            </NavItem>
            
            <NavItem>
              <NavLink href="/crowdsourcing" isScrolled={isScrolled}>
                {translate('nav.crowdsourcing', 'Crowdsource Weather')}
              </NavLink>
            </NavItem>
            
            <NavItem>
              <NavLink href="/#community" isScrolled={isScrolled}>
                {translate('nav.community', 'Community')}
              </NavLink>
            </NavItem>
            
            <NavItem>
              <DropdownContainer>
                <DropdownButton 
                  as="div" 
                  isScrolled={isScrolled}
                  isOpen={docsDropdownOpen}
                  onClick={toggleDocsDropdown}
                >
                  {translate('nav.documentation', 'Documentation')}
                  <span>▼</span>
                </DropdownButton>
                <DropdownContent isOpen={docsDropdownOpen}>
                  <DropdownItem href="/api-docs">
                    {translate('nav.docs.api', 'API Documentation')}
                  </DropdownItem>
                  <DropdownItem href="/how-it-works">
                    {translate('nav.docs.howItWorks', 'How It Works')}
                  </DropdownItem>
                  <DropdownItem href="/contribute">
                    {translate('nav.docs.contribute', 'Contribution Guidelines')}
                  </DropdownItem>
                </DropdownContent>
              </DropdownContainer>
            </NavItem>
            
            <NavItem>
              <NavLink href="/#manual" isScrolled={isScrolled}>
                {translate('nav.userManual', 'User Manual')}
              </NavLink>
            </NavItem>
            
            <NavItem>
              <NavLink href="/faq" isScrolled={isScrolled}>
                {translate('nav.faq', 'Frequently Asked Questions')}
              </NavLink>
            </NavItem>
            
            <NavItem>
              <NavLink 
                href="https://app.weatherteller.com" 
                target="_blank" 
                rel="noopener noreferrer"
                isScrolled={isScrolled}
                style={{ color: props => props.theme.colors.primary, fontWeight: '600' }}
              >
                {translate('nav.app', 'Open App')}
              </NavLink>
            </NavItem>
            <NavItem>
              <DonateButton href="/#donate">
                {translate('donation.nav_button', 'Donate')}
              </DonateButton>
            </NavItem>
            
            <NavItem>
              <DownloadButton href="/#download">
                {translate('nav.getStarted', 'Get Started')}
              </DownloadButton>
            </NavItem>
            
            <NavItem>
              <LanguageSelector isScrolled={isScrolled} />
            </NavItem>
          </NavList>
        </Navigation>
      </HeaderInner>
    </HeaderContainer>
  );
};

export default Header;